import React, { useState, useEffect, useRef } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import useLazyQuery from '../../../../hooks/useLazyQuery';
import styles from './Footer.module.css';
import { POLL_RESULT_IS_CONFIRMED } from '../../../../graphql/Campaigns';

import FooterBackground from '../../../../assets/img/web-survey/FooterBackground';
import personHoldingFlagHerdy from '../../../../assets/img/person-holding-flag-herdy.svg';
import personGivingWaterHerdy from '../../../../assets/img/person-giving-water-herdy.svg';
import dutchFlag from '../../../../assets/img/dutch-flag.svg';
import frenchFlag from '../../../../assets/img/french-flag.svg';
import englishFlag from '../../../../assets/img/english-flag.svg';
import manHoldingPensilHerdy from '../../../../assets/img/man-holding-pencil-herdy.svg';
import womanHoldingPensilHerdy from '../../../../assets/img/woman-holding-pencil-herdy.svg';
import pointingArm from '../../../../assets/img/pointing-arm.svg';
import confirmResultHerdyGroup1 from '../../../../assets/img/confirm-result-herdy-group-1.svg';
import confirmResultHerdyGroup2 from '../../../../assets/img/confirm-result-herdy-group-2.svg';
import confirmResultHerdyGroup3 from '../../../../assets/img/confirm-result-herdy-group-3.svg';

export default props => {
  const {
    blockId,
    resultId,
    blockType,
    onClickNext,
    onClickBack,
    disabledNext,
    hiddenNext,
    backLabel,
    nextLabel,
    language,
    languages,
    page,
    pages,
    campaignLanguage,
    allowMarginTop,
    fixedPosition,
    hideIllustrationsInWebSurvey,
    customThemeBaseColor,
    enableNextAfterSeconds,
    secLabel,
    confirmResultWithApi
  } = props;

  const pollResult = useLazyQuery(POLL_RESULT_IS_CONFIRMED, {
    fetchPolicy: 'no-cache'
  });

  const [remainingSecondsToEnable, setRemainingSecondsToEnable] = useState(
    null
  );
  const timer = useRef();
  const confirmTimer = useRef();

  const FooterBackgroundSvgString = encodeURIComponent(
    renderToStaticMarkup(<FooterBackground fillColor={customThemeBaseColor} />)
  );

  const languageFlags =
    page && pages && languages && page === pages.CAMPAIGN
      ? {
          [languages.ENGLISH]: {
            src: englishFlag,
            alt: 'English flag',
            class: styles.englishFlagOnHerdy
          },
          [languages.FRENCH]: {
            src: frenchFlag,
            alt: 'French flag',
            class: styles.frenchFlagOnHerdy
          },
          [languages.DUTCH]: {
            src: dutchFlag,
            alt: 'Dutch flag',
            class: styles.dutchFlagOnHerdy
          }
        }
      : {};

  useEffect(
    () => {
      if (remainingSecondsToEnable) {
        timer.current = setTimeout(() => {
          const remaining =
            remainingSecondsToEnable === null
              ? enableNextAfterSeconds
              : remainingSecondsToEnable - 1;
          if (remaining > 0) {
            setRemainingSecondsToEnable(remaining);
          } else {
            setRemainingSecondsToEnable(null);
          }
        }, 1000);
      } else {
        setRemainingSecondsToEnable(null);
        if (timer && timer.current) {
          clearTimeout(timer.current);
        }
      }
    },
    [remainingSecondsToEnable]
  );

  useEffect(
    () => {
      if (enableNextAfterSeconds) {
        setRemainingSecondsToEnable(enableNextAfterSeconds);
      }
      if (timer && timer.current) {
        clearTimeout(timer.current);
      }
      if (confirmTimer && confirmTimer.current) {
        clearTimeout(confirmTimer.current);
      }

      if (blockType === 'iframe' && confirmResultWithApi) {
        const pollResultIsConfirmed = async () => {
          const res = await pollResult({
            variables: {
              result: resultId,
              question: blockId
            }
          });

          if (
            res &&
            res.data &&
            res.data.pollResultIsConfirmed &&
            !res.data.pollResultIsConfirmed.done
          ) {
            confirmTimer.current = setTimeout(() => {
              pollResultIsConfirmed();
            }, 2000);
          }

          if (
            res &&
            res.data &&
            res.data.pollResultIsConfirmed &&
            res.data.pollResultIsConfirmed.done
          ) {
            setRemainingSecondsToEnable(null);
            if (timer && timer.current) {
              clearTimeout(timer.current);
            }
            if (confirmTimer && confirmTimer.current) {
              clearTimeout(confirmTimer.current);
            }

            // Proceed with next question
            onClickNext(true);
          }
        };

        pollResultIsConfirmed();
      }
    },
    [blockId]
  );

  useEffect(() => {
    window.addEventListener(
      'message',
      event => {
        if (event && event.data && event.data === 'BUFFL') {
          setRemainingSecondsToEnable(null);
          if (timer && timer.current) {
            clearTimeout(timer.current);
          }
        }
      },
      false
    );
  }, []);

  const isNextDisabled = () => {
    if (remainingSecondsToEnable) {
      return true;
    }

    return disabledNext;
  };

  const nextButtonLabel = () => {
    if (remainingSecondsToEnable) {
      return `${remainingSecondsToEnable} ${secLabel}`;
    }

    return nextLabel;
  };

  return (
    <>
      {!hideIllustrationsInWebSurvey ? (
        <div className={styles.herdiesContainer}>
          {page &&
          pages &&
          page === pages.CAMPAIGN &&
          languageFlags &&
          languageFlags[language] ? (
            <>
              <img
                src={personGivingWaterHerdy}
                alt="Person giving water"
                className={styles.personGivingWaterHerdy}
              />
              <img
                src={languageFlags[language].src}
                alt={languageFlags[language].alt}
                className={languageFlags[language].class}
              />
              <img
                src={personHoldingFlagHerdy}
                alt="Person holding a flag"
                className={styles.personHoldingFlagHerdy}
              />
            </>
          ) : null}
          {page && pages && page === pages.USER_DETAILS ? (
            <>
              <div className={styles.manHoldingPensilHerdyWrapper}>
                <img
                  src={manHoldingPensilHerdy}
                  alt="Man holding a pensil"
                  className={styles.manHoldingPensilHerdy}
                />
              </div>
              <div className={styles.womanHoldingPensilHerdyWrapper}>
                <img
                  src={womanHoldingPensilHerdy}
                  alt="Woman holding a pensil"
                  className={styles.womanHoldingPensilHerdy}
                />
              </div>
            </>
          ) : null}
          {page && pages && page === pages.READY ? (
            <img
              src={pointingArm}
              alt="arm pointing"
              className={styles.pointingArm}
            />
          ) : null}
          {page && pages && page === pages.USER_PROFILE ? (
            <>
              <img
                src={confirmResultHerdyGroup1}
                alt="Group of herdies with phones and flag on left side"
                className={styles.confirmResultHerdyGroup1}
              />
              <img
                src={confirmResultHerdyGroup2}
                alt="Group of herdies with phones on right side"
                className={styles.confirmResultHerdyGroup2}
              />
            </>
          ) : null}
          {page && pages && page === pages.CONFIRMATION ? (
            <img
              src={confirmResultHerdyGroup3}
              alt="Group of herdies with phones on bottom side"
              className={styles.confirmResultHerdyGroup3}
            />
          ) : null}
        </div>
      ) : null}
      <div
        className={`${styles.container} ${
          fixedPosition ? styles.fixedContainer : ''
        }`}
      >
        <div
          className={styles.footerBackground}
          style={{
            backgroundImage: `url("data:image/svg+xml,${FooterBackgroundSvgString}")`
          }}
        />
        <div
          className={`${styles.bottomButtonWrapper} ${
            !allowMarginTop ? styles.noMobileTopMargin : ''
          }`}
        >
          <div className={styles.bottomButtonContainer}>
            <div className={styles.leftButtonContainer}>
              {onClickBack && !confirmResultWithApi ? (
                <div
                  className={`${styles.button} user-campaigns-back-button`}
                  style={{
                    backgroundColor: customThemeBaseColor || '#dccbfd',
                    color: customThemeBaseColor ? '#FFFFFF' : '#4926e9'
                  }}
                  onClick={() => {
                    if (enableNextAfterSeconds) {
                      setRemainingSecondsToEnable(null);
                    }
                    if (timer && timer.current) {
                      clearTimeout(timer.current);
                    }
                    onClickBack();
                  }}
                  role="presentation"
                >
                  {backLabel || 'BACK'}
                </div>
              ) : null}
            </div>
            <div className={styles.rightButtonContainer}>
              {page && pages && page === pages.CAMPAIGN && !campaignLanguage ? (
                <img
                  src={languageFlags[language].src}
                  alt={languageFlags[language].alt}
                  className={`${styles.languageFlagOnNextButton} ${
                    languageFlags[language].class
                  }`}
                />
              ) : null}
              {onClickNext && !hiddenNext && !confirmResultWithApi ? (
                <div
                  style={{
                    backgroundColor: customThemeBaseColor || '#dccbfd',
                    color: customThemeBaseColor ? '#FFFFFF' : '#4926e9'
                  }}
                  className={`${styles.button} ${
                    isNextDisabled() ? styles.buttonDisabled : ''
                  } user-campaigns-next-button`}
                  onClick={() => {
                    if (!isNextDisabled()) {
                      onClickNext();
                    }
                  }}
                  role="presentation"
                >
                  {nextButtonLabel() || 'NEXT'}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
